import React, { useState } from 'react';

const CELL_VALUES = [
  { value: 'CC', label: 'CC', description: 'Molt Consistent', color: 'ach-cc', score: 0 },
  { value: 'C', label: 'C', description: 'Consistent', color: 'ach-c', score: 0 },
  { value: 'N', label: 'N', description: 'Neutral', color: 'ach-n', score: 0 },
  { value: 'I', label: 'I', description: 'Inconsistent', color: 'ach-i', score: -1 },
  { value: 'II', label: 'II', description: 'Molt Inconsistent', color: 'ach-ii', score: -2 }
];

const getCellStyle = (value) => {
  const cellValue = CELL_VALUES.find(cv => cv.value === value);
  return cellValue ? cellValue.color : 'bg-white';
};

function ACHMatrix({ hypotheses, evidence, cells, onAddHypothesis, onEditHypothesis, onDeleteHypothesis, onUpdateCell, isReadOnly }) {
  const [newHypothesisText, setNewHypothesisText] = useState('');
  const [editingHypothesis, setEditingHypothesis] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [activeCell, setActiveCell] = useState(null);

  // Create a map for quick cell lookup
  const cellMap = {};
  cells.forEach(cell => {
    cellMap[`${cell.hypothesisId}-${cell.evidenceId}`] = cell;
  });

  // Calculate scores for each hypothesis
  const hypothesisScores = hypotheses.map(h => {
    let score = 0;
    evidence.forEach(e => {
      const cell = cellMap[`${h.id}-${e.id}`];
      if (cell && cell.rating) {
        const cellValue = CELL_VALUES.find(cv => cv.value === cell.rating);
        if (cellValue) {
          score += cellValue.score;
        }
      }
    });
    return { hypothesisId: h.id, score };
  });

  // Find the best hypothesis (least negative score)
  const bestScore = Math.max(...hypothesisScores.map(hs => hs.score));
  const bestHypotheses = hypothesisScores.filter(hs => hs.score === bestScore).map(hs => hs.hypothesisId);

  const handleAddHypothesis = () => {
    if (newHypothesisText.trim()) {
      onAddHypothesis({ description: newHypothesisText.trim() });
      setNewHypothesisText('');
    }
  };

  const handleSaveHypothesis = () => {
    if (editingHypothesis && editingHypothesis.description.trim()) {
      onEditHypothesis(editingHypothesis.id, { description: editingHypothesis.description.trim() });
      setEditingHypothesis(null);
    }
  };

  const handleCellClick = (hypothesisId, evidenceId) => {
    if (isReadOnly) return;
    setActiveCell({ hypothesisId, evidenceId });
  };

  const handleCellValueSelect = (value) => {
    if (activeCell) {
      onUpdateCell({
        hypothesisId: activeCell.hypothesisId,
        evidenceId: activeCell.evidenceId,
        rating: value
      });
      setActiveCell(null);
    }
  };

  if (evidence.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-surface">
        <div className="text-center py-8">
          <svg className="w-16 h-16 mx-auto text-content-disabled mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
          <p className="text-content-muted">No hi ha evidències per analitzar</p>
          <p className="text-sm text-content-disabled mt-1">Les evidències es mostren al panell dret</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface">
      {/* Header */}
      <div className="p-4 border-b border-border bg-surface">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-content flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              Matriu ACH
            </h3>
            <p className="text-xs text-content-muted mt-1">
              Mètode de Disconfirmació: només les inconsistències resten punts
            </p>
          </div>
        </div>

        {/* Add hypothesis */}
        {!isReadOnly && (
          <div className="mt-3 flex items-center space-x-2">
            <input
              type="text"
              value={newHypothesisText}
              onChange={(e) => setNewHypothesisText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddHypothesis()}
              placeholder="Nova hipòtesi..."
              className="flex-1 px-3 py-1.5 text-sm border border-border-strong rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content placeholder-content-disabled"
            />
            <button
              onClick={handleAddHypothesis}
              disabled={!newHypothesisText.trim()}
              className="px-3 py-1.5 text-sm bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Afegir
            </button>
          </div>
        )}
      </div>

      {/* Matrix */}
      <div className="flex-1 overflow-auto p-4">
        {hypotheses.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-content-muted">No hi ha hipòtesis definides</p>
            {!isReadOnly && (
              <p className="text-sm text-content-disabled mt-1">Afegeix hipòtesis per comparar-les amb les evidències</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-surface-muted border border-border-strong p-2 text-left text-sm font-medium text-content-secondary min-w-[200px]">
                    Evidències / Hipòtesis
                  </th>
                  {hypotheses.map(h => {
                    const scoreData = hypothesisScores.find(hs => hs.hypothesisId === h.id);
                    const isBest = bestHypotheses.includes(h.id);
                    return (
                      <th
                        key={h.id}
                        className={`border border-border-strong p-2 text-center text-sm font-medium min-w-[120px] ${isBest ? 'bg-success-subtle' : 'bg-surface-muted'}`}
                      >
                        <div className="flex flex-col items-center">
                          {editingHypothesis?.id === h.id ? (
                            <div className="w-full">
                              <input
                                type="text"
                                value={editingHypothesis.description}
                                onChange={(e) => setEditingHypothesis({ ...editingHypothesis, description: e.target.value })}
                                onKeyPress={(e) => e.key === 'Enter' && handleSaveHypothesis()}
                                className="w-full px-2 py-1 text-sm border border-border-strong rounded bg-surface text-content"
                                autoFocus
                              />
                              <div className="flex justify-center mt-1 space-x-1">
                                <button
                                  onClick={handleSaveHypothesis}
                                  className="text-xs text-success hover:text-success-text"
                                >
                                  Guardar
                                </button>
                                <button
                                  onClick={() => setEditingHypothesis(null)}
                                  className="text-xs text-content-muted hover:text-content-secondary"
                                >
                                  Cancel·lar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <span className="text-content">{h.description}</span>
                              <span className={`mt-1 px-2 py-0.5 text-xs rounded ${scoreData?.score === 0 ? 'bg-surface-muted text-content-secondary' : scoreData?.score < 0 ? 'bg-error-subtle text-error-text' : 'bg-success-subtle text-success-text'}`}>
                                {scoreData?.score || 0}
                              </span>
                              {!isReadOnly && (
                                <div className="flex mt-1 space-x-2">
                                  <button
                                    onClick={() => setEditingHypothesis({ id: h.id, description: h.description })}
                                    className="text-xs text-content-disabled hover:text-content-secondary"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    onClick={() => setShowDeleteModal(h)}
                                    className="text-xs text-error hover:text-error"
                                  >
                                    Eliminar
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {evidence.map(e => (
                  <tr key={e.id}>
                    <td className="sticky left-0 z-10 bg-surface border border-border-strong p-2">
                      <div className="flex items-center">
                        <span className="font-mono text-sm text-accent font-medium mr-2">{e.code}</span>
                        <span className="text-sm text-content-secondary truncate">{e.title}</span>
                      </div>
                    </td>
                    {hypotheses.map(h => {
                      const cell = cellMap[`${h.id}-${e.id}`];
                      const cellRating = cell?.rating;
                      const isActive = activeCell?.hypothesisId === h.id && activeCell?.evidenceId === e.id;
                      return (
                        <td
                          key={`${h.id}-${e.id}`}
                          className={`border border-border-strong p-2 text-center cursor-pointer transition-colors ${getCellStyle(cellRating)} ${isActive ? 'ring-2 ring-primary-500' : ''} ${!isReadOnly ? 'hover:bg-surface-subtle' : ''}`}
                          onClick={() => handleCellClick(h.id, e.id)}
                        >
                          <span className="font-medium">{cellRating || '-'}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-border bg-surface-subtle">
        <div className="flex items-center justify-center flex-wrap gap-3 text-xs">
          {CELL_VALUES.map(cv => (
            <div key={cv.value} className="flex items-center">
              <span className={`px-2 py-1 rounded ${cv.color} font-medium mr-1`}>{cv.label}</span>
              <span className="text-content-secondary">{cv.description} ({cv.score})</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cell Value Selector */}
      {activeCell && !isReadOnly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setActiveCell(null)}>
          <div className="bg-surface rounded-lg shadow-xl p-4" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-medium text-content mb-3">Selecciona consistència</h4>
            <div className="flex space-x-2">
              {CELL_VALUES.map(cv => (
                <button
                  key={cv.value}
                  onClick={() => handleCellValueSelect(cv.value)}
                  className={`px-4 py-2 rounded ${cv.color} font-medium hover:opacity-80 transition-opacity`}
                  title={cv.description}
                >
                  {cv.label}
                </button>
              ))}
              <button
                onClick={() => handleCellValueSelect(null)}
                className="px-4 py-2 rounded bg-surface-muted text-content-secondary font-medium hover:bg-surface-muted"
                title="Esborrar"
              >
                X
              </button>
            </div>
            <p className="mt-3 text-xs text-content-muted text-center">
              Clica fora per cancel·lar
            </p>
          </div>
        </div>
      )}

      {/* Delete Hypothesis Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-content">Eliminar Hipòtesi</h3>
            </div>
            <div className="p-4">
              <p className="text-content-secondary">
                Estàs segur que vols eliminar la hipòtesi <strong className="text-content">"{showDeleteModal.description}"</strong>?
              </p>
              <p className="text-sm text-content-muted mt-2">
                S'eliminaran totes les valoracions associades.
              </p>
            </div>
            <div className="p-4 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded hover:bg-surface-subtle"
              >
                Cancel·lar
              </button>
              <button
                onClick={() => {
                  onDeleteHypothesis(showDeleteModal.id);
                  setShowDeleteModal(null);
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ACHMatrix;
